/**
 * PublicResumePage — /{handle}/resume
 *
 * Public read-only view of any member's resume. Consumed by
 * alumnipulse, indiaalumni, indianedupulse, and every geographical
 * portal's career/education section.
 *
 * The rendering is the SAME JSON that powers the PDF; this is just an
 * HTML mirror so search engines + link previews can index the resume.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Download, ChevronLeft, Loader2, Globe, Github, Linkedin, Mail, Phone, MapPin } from "lucide-react";

function fmtDate(d) {
  if (!d) return "";
  const [y, m] = String(d).split("-");
  if (!m) return y || d;
  const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${names[+m - 1] || m} ${y}`;
}

function DateRange({ start, end, current }) {
  const s = fmtDate(start);
  const e = current || !end ? "Present" : fmtDate(end);
  if (!s && !e) return null;
  return <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">{s} — {e}</span>;
}

export default function PublicResumePage() {
  const { handle } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!handle) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/resume/${handle}`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.status === 403
          ? "This resume is private."
          : "Resume not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [handle]);

  const pdfUrl = useMemo(() => `${API}/resume/${handle}/pdf`, [handle]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-600">
        <div className="text-center">
          <p className="mb-2">{error}</p>
          <Link to={`/${handle}`} className="text-indigo-600 underline">Back to profile</Link>
        </div>
      </div>
    );
  }
  const r = data.resume;
  const port = data.portfolio || [];
  const accent = r.accent_color || "#4f46e5";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-resume-page">
      {/* Header bar */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to={`/${handle}`} className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-sm">
            <ChevronLeft className="w-4 h-4" /> Profile
          </Link>
          <div className="flex-1 text-sm text-slate-600 truncate">Resume · <span className="text-slate-900 font-medium">{r.contact?.name || handle}</span></div>
          <a href={pdfUrl} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-white"
             style={{ background: accent }} data-testid="download-pdf-btn">
            <Download className="w-4 h-4" /> PDF
          </a>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <article className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {/* Header block */}
          <header className="px-6 py-8 border-b" style={{ borderBottomColor: accent, borderBottomWidth: 3 }}>
            <h1 className="text-3xl font-semibold text-slate-900 mb-1" style={{ color: accent }}>{r.contact?.name}</h1>
            {r.contact?.headline && <p className="text-lg text-slate-600 mb-3">{r.contact.headline}</p>}
            <div className="flex flex-wrap gap-3 text-sm text-slate-600">
              {r.contact?.email && <a href={`mailto:${r.contact.email}`} className="inline-flex items-center gap-1 hover:text-indigo-600"><Mail className="w-3.5 h-3.5" />{r.contact.email}</a>}
              {r.contact?.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{r.contact.phone}</span>}
              {r.contact?.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{r.contact.location}</span>}
              {r.contact?.website && <a href={r.contact.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-indigo-600"><Globe className="w-3.5 h-3.5" />Website</a>}
              {r.contact?.linkedin && <a href={r.contact.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-indigo-600"><Linkedin className="w-3.5 h-3.5" />LinkedIn</a>}
              {r.contact?.github && <a href={r.contact.github} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-indigo-600"><Github className="w-3.5 h-3.5" />GitHub</a>}
            </div>
          </header>

          <div className="px-6 py-6 space-y-8">
            {r.summary && (
              <Section title="Summary" accent={accent}>
                <p className="text-slate-700 leading-relaxed whitespace-pre-line">{r.summary}</p>
              </Section>
            )}

            {r.experience?.length > 0 && (
              <Section title="Experience" accent={accent}>
                {r.experience.map((e, i) => (
                  <Entry key={i} title={e.title} sub={e.company} location={e.location}
                         start={e.start_date} end={e.end_date} current={e.current}
                         description={e.description} bullets={e.achievements} />
                ))}
              </Section>
            )}

            {r.education?.length > 0 && (
              <Section title="Education" accent={accent}>
                {r.education.map((e, i) => (
                  <Entry key={i} title={e.institution} sub={[e.degree, e.field].filter(Boolean).join(" · ")}
                         location={e.location} start={e.start_date} end={e.end_date}
                         description={e.description} />
                ))}
              </Section>
            )}

            {r.skills?.length > 0 && (
              <Section title="Skills" accent={accent}>
                {r.skills.map((g, i) => (
                  <div key={i} className="mb-2">
                    {g.category && <div className="text-sm font-medium text-slate-700 mb-1">{g.category}</div>}
                    <div className="flex flex-wrap gap-1.5">
                      {g.items.map((s, j) => (
                        <span key={j} className="px-2 py-0.5 rounded text-sm" style={{ background: `${accent}15`, color: accent }}>{s}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </Section>
            )}

            {port.length > 0 && (
              <Section title="Portfolio" accent={accent}>
                <div className="grid sm:grid-cols-2 gap-3">
                  {port.map(p => (
                    <a key={p.id} href={p.external_url || p.live_url || "#"} target="_blank" rel="noreferrer"
                       className="border rounded-md p-3 hover:shadow-sm block">
                      <div className="text-sm font-medium text-slate-900 truncate">{p.title}</div>
                      {p.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{p.description}</div>}
                      {p.technologies?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {p.technologies.slice(0, 5).map((t, i) => (
                            <span key={i} className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded">{t}</span>
                          ))}
                        </div>
                      )}
                    </a>
                  ))}
                </div>
              </Section>
            )}

            {r.projects?.length > 0 && (
              <Section title="Projects" accent={accent}>
                {r.projects.map((p, i) => (
                  <Entry key={i} title={p.name} link={p.url} start={p.start_date} end={p.end_date}
                         description={p.description} chips={p.technologies} accent={accent} />
                ))}
              </Section>
            )}

            {r.certifications?.length > 0 && (
              <Section title="Certifications" accent={accent}>
                {r.certifications.map((c, i) => (
                  <Entry key={i} title={c.name} sub={c.issuer} start={c.date} link={c.url} />
                ))}
              </Section>
            )}

            {r.awards?.length > 0 && (
              <Section title="Awards" accent={accent}>
                {r.awards.map((a, i) => (
                  <Entry key={i} title={a.title} sub={a.issuer} start={a.date} description={a.description} />
                ))}
              </Section>
            )}

            {r.publications?.length > 0 && (
              <Section title="Publications" accent={accent}>
                {r.publications.map((p, i) => (
                  <Entry key={i} title={p.title} sub={p.publisher} start={p.date} link={p.url} description={p.description} />
                ))}
              </Section>
            )}

            {r.languages?.length > 0 && (
              <Section title="Languages" accent={accent}>
                <div className="flex flex-wrap gap-2 text-sm">
                  {r.languages.map((l, i) => (
                    <span key={i} className="text-slate-700">{l.name}{l.level && <span className="text-slate-400"> · {l.level}</span>}</span>
                  ))}
                </div>
              </Section>
            )}
          </div>
        </article>
      </div>
    </div>
  );
}

function Section({ title, accent, children }) {
  return (
    <section>
      <h2 className="text-xs uppercase tracking-wider font-semibold mb-3 pb-1 border-b" style={{ color: accent, borderBottomColor: `${accent}30` }}>{title}</h2>
      {children}
    </section>
  );
}

function Entry({ title, sub, location, start, end, current, description, bullets, chips, accent, link }) {
  return (
    <div className="mb-4 last:mb-0">
      <div className="flex justify-between items-baseline gap-3">
        <div>
          <div className="text-sm">
            {link ? <a href={link} className="font-semibold text-slate-900 hover:underline" target="_blank" rel="noreferrer">{title}</a>
                  : <span className="font-semibold text-slate-900">{title}</span>}
            {sub && <span className="text-slate-500"> · {sub}</span>}
          </div>
          {location && <div className="text-xs text-slate-500">{location}</div>}
        </div>
        <DateRange start={start} end={end} current={current} />
      </div>
      {description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{description}</p>}
      {bullets?.length > 0 && (
        <ul className="mt-1 ml-4 list-disc text-sm text-slate-600 space-y-0.5">
          {bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
      {chips?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {chips.map((c, i) => (
            <span key={i} className="text-xs px-1.5 py-0.5 rounded" style={{ background: `${accent || "#4f46e5"}15`, color: accent || "#4f46e5" }}>{c}</span>
          ))}
        </div>
      )}
    </div>
  );
}
